import { gql, useLazyQuery } from "@apollo/client"
import { Avatar, Select } from "@oysterhr/ui"

import debounce from "lodash-es/debounce"
import { ComponentProps, useEffect } from "react"
import { Query, User } from "@oysterhr/schema"

type OysterAdmin = Pick<User, "id" | "name" | "email" | "avatarUrl">

type NewSelectOysterAdminProps = ComponentProps<typeof Select> & {
  defaultOptions?: Array<Partial<OysterAdmin>>
}

export const GENERAL_STAFF_OYSTER_ADMIN_SELECT = gql`
  query GeneralStaffGetOysterAdminSelect($query: String!) {
    users(isOysterAdmin: true, perPage: 5, query: $query, order: "name asc") {
      collection {
        email
        id
        name
        avatarUrl
      }
    }
  }
`

export const NewSelectOysterAdmin = ({
  value,
  defaultOptions,
  ...rest
}: NewSelectOysterAdminProps) => {
  const [searchUser, { data, loading, previousData }] = useLazyQuery<
    { users: Query["users"] },
    { query?: string }
  >(GENERAL_STAFF_OYSTER_ADMIN_SELECT, {
    variables: { query: value ?? "" },
  })

  useEffect(() => {
    if (value && !defaultOptions) {
      searchUser({ variables: { query: value } })
    } else if (!value) {
      searchUser()
    }
  }, [defaultOptions, searchUser, value])

  const handleSearch = debounce<React.ChangeEventHandler<HTMLInputElement>>(e => {
    searchUser({ variables: { query: e.target.value } })
  }, 250)

  // Avoid showing empty options list between searches
  const users = (
    data?.users.collection ||
    previousData?.users.collection ||
    defaultOptions ||
    []
  ).filter(user => !!user?.id)

  return (
    <Select
      value={value}
      isClearable
      {...rest}
      emptyState={<Select.EmptyState>No results found with that user name</Select.EmptyState>}
    >
      <Select.Input placeholder="Search" isLoading={loading} onChange={handleSearch} />
      {users.map(user => {
        return (
          <Select.Option
            value={user.id}
            key={user.id}
            startAdornment={<Avatar name={user.name} src={user.avatarUrl} />}
          >
            {user.name}
          </Select.Option>
        )
      })}
    </Select>
  )
}
